export const CHECKOUT_URL = 'https://operating-en.impultienda.ar/checkout/';

export const images = {
  author: 'https://cdn.magicpatterns.com/uploads/1KXPgsF9AZ6yoCYKpxAJuM/Ethan_Whitmore_-_RHINOPLASTY.png',
  bundle: 'https://cdn.magicpatterns.com/uploads/krdqBjEP5cpPt7YREpPuha/OPERATING-EN.png',
};

export const storyItems = [
  'Patient information',
  'Photographs',
  'Consultation notes',
  'Case preparation',
  'Team communication',
  'Documentation',
  'Follow-up',
  'Reviews',
  'Templates',
  'Checklists',
];

export const clarityItems = [
  'What happens first.',
  'What needs to be documented.',
  'What needs to be reviewed.',
  'What can be standardized.',
  'What can be delegated.',
  'What should be checked.',
];

export const beforeItems = [
  '“I already have a system. It’s just mostly in my head.”',
  'Templates scattered across folders, notes and devices',
  'Recreating the same documents over and over again',
  'Every case starts from a blank page',
  'A workflow that evolved but was never documented',
  'Remembering every repeating step from memory',
];

export const afterItems = [
  'Your entire workflow mapped and visible in one place',
  'Recurring processes backed by standardized checklists',
  'Reusable templates for your most common documents',
  'Structured case briefs ready before every case',
  'A review framework that turns cases into learning',
  'A documented operating system that is truly yours',
];

export type Volume = {
  number: number;
  title: string;
  headline: string;
  intro: string;
  items: string[];
  goal: string;
  accent: string;
};

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'The Rhino Workflow™',
    headline: 'Let’s start by mapping your entire workflow.',
    intro:
      'Before you can improve a process, you need to see it. Map your workflow from initial consultation through case preparation, documentation, follow-up and review.',
    items: [
      'Complete Rhino Workflow Map',
      'Professional Process Audit',
      'Workflow Gap Finder',
      'Case Journey Overview',
      'Daily Workflow Planner',
      'Weekly Practice Review',
      'Process Documentation Guide',
      'Personal Workflow Builder',
      'Workflow Organization Framework',
    ],
    goal: 'Give you a clear picture of how your practice actually works.',
    accent: '#2f5d8a',
  },
  {
    number: 2,
    title: 'The Consultation System™',
    headline: 'Because good information shouldn’t get lost between notes.',
    intro:
      'Patient goals, questions, expectations, clinical considerations, photographs and documentation — organized so you can use them consistently.',
    items: [
      'Consultation Preparation Checklist',
      'Patient Goals Worksheet',
      'Consultation Question Framework',
      'Aesthetic & Functional Considerations Map',
      'Patient Information Organizer',
      'Photography Documentation Checklist',
      'Consultation Summary Template',
      'Case Information Sheet',
      'Follow-Up Preparation Checklist',
    ],
    goal: 'A repeatable structure for the information generated during consultation.',
    accent: '#2d7a6c',
  },
  {
    number: 3,
    title: 'The Case Planning Playbook™',
    headline: 'Stop starting every case from a blank page.',
    intro:
      'A framework for building a structured case brief — so important information lives in one place.',
    items: [
      'Case Brief Template',
      'Case Information Dashboard',
      'Planning Worksheet',
      'Priorities Matrix',
      'Questions-to-Resolve Framework',
      'Pre-Case Review Checklist',
      'Team Briefing Template',
      'Case Preparation Timeline',
      'Case Documentation System',
    ],
    goal: 'Turn the information you’ve collected into a usable case-planning structure.',
    accent: '#a0672a',
  },
  {
    number: 4,
    title: 'The Professional Checklist System™',
    headline: 'You shouldn’t have to remember every repeating step.',
    intro:
      'Checklists make invisible processes visible. Build a repeatable structure around the things you do regularly.',
    items: [
      'Consultation Checklist',
      'Documentation Checklist',
      'Photography Checklist',
      'Case Preparation Checklist',
      'Team Preparation Checklist',
      'Pre-Case Review Checklist',
      'Post-Case Documentation Checklist',
      'Follow-Up Checklist',
      'Monthly Process Audit',
    ],
    goal: 'Build your own checklist library instead of recreating the same process.',
    accent: '#6b4a9a',
  },
  {
    number: 5,
    title: 'The Case Review System™',
    headline: 'Your work doesn’t have to end when the case is finished.',
    intro:
      'What happened? What was expected? What was different? What did you learn? A structured way to review your own process.',
    items: [
      'Case Review Framework',
      'Pre-Case vs. Post-Case Comparison',
      'Case Documentation Sheet',
      'What Worked / What Changed Review',
      'Lessons Learned Worksheet',
      'Pattern Recognition Journal',
      'Monthly Case Review',
      'Practice Improvement Tracker',
      'Personal Case Library Framework',
    ],
    goal: 'Turn completed cases into structured opportunities for improvement.',
    accent: '#9a2f2f',
  },
  {
    number: 6,
    title: 'The Practice Operating System™',
    headline: 'This is where everything comes together.',
    intro:
      'You’ve mapped, organized, structured, checklisted and reviewed. Now connect everything into one system that belongs to your practice.',
    items: [
      'Master Workflow',
      'Master Dashboard',
      'Practice SOP Builder',
      'Delegation Framework',
      'Team Communication Templates',
      'Weekly Review System',
      'Monthly Practice Audit',
      'Process Improvement Framework',
      'Personal Operating Manual',
      '90-Day Implementation Roadmap',
    ],
    goal: 'Turn individual tools into one system that actually belongs to you.',
    accent: '#8f6c1c',
  },
];

export type Bonus = {
  number: number;
  title: string;
  subtitle: string;
  description: string;
  items: string[];
  value: number;
  image: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Rhino Quick Reference™',
    subtitle: '50 visual reference resources for your workflow',
    description:
      'Your quick-access library. Instead of searching through an entire manual, open the frameworks and reference sheets you use most.',
    items: ['Workflow Map', 'Consultation Map', 'Case Brief Map', 'Review Map', 'Planning Framework', 'Checklist Index', 'Quick Review Cards'],
    value: 47,
    image: 'https://cdn.magicpatterns.com/uploads/h8NMbBHLvZ4J3sXWMc6M8a/BONUS_1_OPERATING-EN.png',
  },
  {
    number: 2,
    title: 'Rhino Master Checklist™',
    subtitle: 'The complete professional checklist library',
    description:
      'Ready-to-customize checklist templates covering every major stage of your workflow.',
    items: ['Consultation', 'Documentation', 'Photography', 'Case Preparation', 'Team Preparation', 'Pre-Case Review', 'Post-Case Review', 'Follow-Up', 'Practice Audit'],
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/gHzh7aAzc2ZPQUJ2rBphF1/BONUS_2_OPERATING-EN.png',
  },
  {
    number: 3,
    title: 'Rhino Case Review™',
    subtitle: 'The case learning & review toolkit',
    description:
      'Reviewing your process shouldn’t require building a new document every time.',
    items: ['Case Review', 'Pre/Post Comparison', 'Lessons Learned', 'Process Review', 'Improvement Tracking', 'Case Archiving'],
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/hPKKM2M3XgRgkWgzew4BGW/BONUS_3_OPERATING-EN.png',
  },
  {
    number: 4,
    title: 'Rhino 90-Day Implementation Plan™',
    subtitle: 'Don’t just download the Playbook. Build your system.',
    description:
      'The resource I would use if I were starting from scratch — work through the system progressively.',
    items: ['Week-by-week implementation plan', 'Priority Matrix', 'Workflow Audit', 'Implementation Tracker', 'Monthly Review', '90-Day Completion Checklist'],
    value: 47,
    image: 'https://cdn.magicpatterns.com/uploads/6Q2WKMexx7KHpQuSoqqWSi/BONUS_4_OPERATING-EN.png',
  },
  {
    number: 5,
    title: 'Rhino Practice Audit™',
    subtitle: 'Step back and see your practice from the outside.',
    description:
      'Examine what’s become “normal” simply because you’ve done it for years — then create a prioritized action plan.',
    items: ['Consultation', 'Documentation', 'Case Preparation', 'Team Workflow', 'Follow-Up', 'Practice Systems', 'Improvement Opportunities'],
    value: 57,
    image: 'https://cdn.magicpatterns.com/uploads/oEQSjMaVZQvNnGTbuJk15w/BONUS_5_OPERATING.png',
  },
];

export const planSteps = [
  { days: 'Days 1–5', task: 'Map your current workflow.' },
  { days: 'Days 6–10', task: 'Organize your consultation process.' },
  { days: 'Days 11–15', task: 'Build your case-planning templates.' },
  { days: 'Days 16–20', task: 'Create your core checklist library.' },
  { days: 'Days 21–25', task: 'Build your case-review process.' },
  { days: 'Days 26–30', task: 'Connect everything into your personal operating system.' },
];

export const audiences = [
  { title: 'Plastic Surgeons', text: 'A more structured way to organize recurring processes.' },
  { title: 'ENT Specialists', text: 'Practical frameworks for documentation, preparation, review and workflow organization.' },
  { title: 'Facial Plastic Surgeons', text: 'Turn your existing processes into more repeatable systems.' },
  { title: 'Experienced Practitioners', text: 'Document, refine and improve the workflow you already have.' },
  { title: 'Developing Specialists', text: 'Begin building organized systems as your practice evolves.' },
];

export const notInside = [
  'Hours and hours of lectures',
  'Complicated software',
  'Learning programming',
  'Rebuilding your practice overnight',
];

export const inside = ['Frameworks', 'Checklists', 'Templates', 'Workflows', 'Review systems', 'Implementation tools'];

export const testimonials = [
  {
    name: 'Dr. Alejandro Vargas',
    role: 'Plastic Surgeon · Miami, USA',
    quote:
      'I finally have my consultation-to-follow-up workflow on paper. My team stopped asking me the same questions every week because the checklists answer them.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/4e9f75c7-2d7c-416f-b669-8d50cf5c510b.jpg',
  },
  {
    name: 'Dr. Laura Mendes',
    role: 'Facial Plastic Surgeon · Toronto, Canada',
    quote:
      'The Case Brief Template alone was worth it. I no longer start a case from scattered notes — everything I need is in one structured page.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9e403ad7-c8ef-4001-bcb4-a9ed6b38c7d6.jpg',
  },
  {
    name: 'Dr. Richard Hale',
    role: 'ENT Specialist · London, UK',
    quote:
      'Practical, not theoretical. I adapted the documentation and photography checklists in a weekend and rolled them out to my staff on Monday.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a107607c-e233-41b4-beca-bb697e64884a.jpg',
  },
  {
    name: 'Dr. Amara Okafor',
    role: 'Plastic Surgeon · Houston, USA',
    quote:
      'The Case Review System changed how I reflect on my work. I now keep a monthly review and I can actually see patterns I used to miss.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ab849bb5-3b5a-4822-9634-19dc45debc2c.jpg',
  },
  {
    name: 'Dr. Marco Rossi',
    role: 'Developing Specialist · Sydney, Australia',
    quote:
      'Early in my practice, this gave me a structure I would have taken years to build. I hit the 30-day target: 12 checklists and 6 templates.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/19c335a4-4abd-48f6-b912-929b52ddcd04.jpg',
  },
];

export const faqs = [
  {
    q: 'Ethan, what exactly am I getting?',
    a: 'You’re getting the complete digital RHINO OPERATING PLAYBOOK™, consisting of six volumes covering workflow organization, consultation, case planning, checklists, case review and practice operations, plus five bonus resources.',
  },
  { q: 'Is this a video course?', a: 'No. I intentionally designed this as a practical digital playbook rather than another long video course.' },
  { q: 'How quickly can I start?', a: 'Immediately. Once you have access, you can begin with Volume 1 and start mapping your current workflow.' },
  { q: 'Do I need special software?', a: 'No. The system is designed around practical digital documents, worksheets, checklists and templates.' },
  { q: 'Can I customize the templates?', a: 'Absolutely. In fact, I expect you to. The goal is to adapt the system to your own professional workflow.' },
  {
    q: 'Is this intended for medical professionals?',
    a: 'Yes. The RHINO OPERATING PLAYBOOK™ is intended for qualified professionals working within the relevant medical context.',
  },
  {
    q: 'Does this teach surgical techniques?',
    a: 'No. This product focuses on workflow organization, documentation, planning processes, checklists and professional review systems. It is not a substitute for formal medical education, supervised clinical training or professional judgment.',
  },
  {
    q: 'Can this replace my existing systems?',
    a: 'You don’t have to replace everything. Use the parts that are useful to you and adapt them around the systems you already have.',
  },
  {
    q: 'How long should implementation take?',
    a: 'The recommended implementation is structured around 30 days, but you can move faster or slower depending on your schedule and existing workflow.',
  },
  {
    q: 'In which currency will I pay?',
    a: 'The price is shown in USD. At checkout, the amount is converted automatically to your local currency.',
  },
];

export const buyers = [
  { name: 'Carlos M.', country: 'Mexico' },
  { name: 'Valentina R.', country: 'Colombia' },
  { name: 'Javier L.', country: 'Spain' },
  { name: 'Sofía G.', country: 'Argentina' },
  { name: 'Andrés P.', country: 'Chile' },
  { name: 'Camila T.', country: 'Peru' },
  { name: 'Diego F.', country: 'Uruguay' },
  { name: 'Lucía H.', country: 'Ecuador' },
  { name: 'Mateo S.', country: 'Costa Rica' },
  { name: 'Isabella C.', country: 'Dominican Republic' },
  { name: 'Ricardo V.', country: 'Venezuela' },
  { name: 'Daniela O.', country: 'Guatemala' },
  { name: 'Fernando B.', country: 'Panama' },
  { name: 'Mariana J.', country: 'Puerto Rico' },
  { name: 'Gabriel N.', country: 'Bolivia' },
  { name: 'Paula E.', country: 'Paraguay' },
];
